var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { cloneElement, forwardRef, useRef, useEffect, } from 'react';
import { useButton } from '@react-aria/button';
import { useDialog } from '@react-aria/dialog';
import { DismissButton, OverlayContainer, useModal, useOverlay, useOverlayPosition, useOverlayTrigger, } from '@react-aria/overlays';
import { mergeProps } from '@react-aria/utils';
import { useOverlayTriggerState } from '@react-stately/overlays';
import { useFocusTrapping } from '../../utils/hooks/useFocusTrapping';
import { tvMerge } from '../../utils/tailwind';
const PopoverContent = forwardRef(({ title, children, isOpen, onClose, style, id, className }, ref) => {
    const { overlayProps } = useOverlay({
        onClose,
        isOpen,
        isDismissable: true,
    }, ref);
    const { modalProps } = useModal();
    const { dialogProps } = useDialog({}, ref);
    useFocusTrapping(ref);
    return (_jsxs("div", Object.assign({ "aria-label": title }, mergeProps(overlayProps, dialogProps, { id }, modalProps), { ref: ref, className: tvMerge([
            'shadow-low rounded-8 bg-background-overlay dark:bg-background-overlay-dark outline-none',
            className,
        ]), style: Object.assign({}, style), "aria-modal": true, children: [children, _jsx(DismissButton, { onDismiss: onClose })] })));
});
const DEFAULT_PLACEMENT = 'bottom';
const DEFAULT_OFFSET = 10;
export const BasePopover = ({ trigger, placement = DEFAULT_PLACEMENT, offset = DEFAULT_OFFSET, title, onToggle, children, isOpen, className, }, ref) => {
    const state = useOverlayTriggerState({});
    const internalTriggerRef = useRef(null);
    const triggerRef = ref || internalTriggerRef;
    const overlayRef = useRef(null);
    const { triggerProps, overlayProps } = useOverlayTrigger({ type: 'dialog' }, state, triggerRef);
    const { overlayProps: positionProps } = useOverlayPosition({
        targetRef: triggerRef,
        overlayRef,
        placement,
        offset,
        isOpen: state.isOpen,
    });
    const _a = useButton({
        onPress: () => {
            state.open();
            onToggle === null || onToggle === void 0 ? void 0 : onToggle(true);
        },
    }, triggerRef).buttonProps, { onFocus, onBlur } = _a, otherButtonProps = __rest(_a, ["onFocus", "onBlur"]);
    useEffect(() => {
        if (isOpen !== undefined) {
            isOpen ? state.open() : state.close();
        }
    }, [isOpen, state]);
    return (_jsxs(_Fragment, { children: [cloneElement(trigger, Object.assign(Object.assign({ ref: triggerRef }, otherButtonProps), { 'aria-controls': triggerProps['aria-controls'], 'aria-expanded': triggerProps['aria-expanded'], 'aria-haspopup': triggerProps['aria-haspopup'] })), state.isOpen && (_jsx(OverlayContainer, { children: _jsx(PopoverContent, Object.assign({}, overlayProps, positionProps, { ref: overlayRef, title: title, isOpen: state.isOpen, onClose: () => {
                        var _a;
                        state.close();
                        (_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                        onToggle === null || onToggle === void 0 ? void 0 : onToggle(false);
                    }, className: className, children: children })) }))] }));
};
export const Popover = forwardRef(BasePopover);
